import { type CSSProperties, type ReactElement } from 'react';

/**
 * OAuth 2.0 consent screen. Rendered full-screen (behind the app's sign-in gate) when a third-party
 * service sends the user to Lapo to authorize. Presentational only — the caller loads the consent
 * metadata, mints the authorization code on Approve, and performs the redirect on Approve/Deny. Styled
 * with the theme's `--lapo-*` variables so it tracks light/dark without extra CSS.
 */
export interface OAuthConsentScope {
  scope: string;
  title: string;
  description: string;
}

export interface OAuthConsentProps {
  /** The signed-in user's email, so they can confirm which account they're granting access to. */
  userEmail?: string;
  /** Requesting client's display name (from registration). Undefined while loading. */
  clientName?: string;
  scopes: OAuthConsentScope[];
  /** Consent metadata is still loading. */
  loading?: boolean;
  /** A blocking error (e.g. unknown client / redirect mismatch) — approval is not offered. */
  error?: string;
  /** Approve/redirect in flight — buttons disabled. */
  busy?: boolean;
  onApprove: () => void;
  onDeny: () => void;
}

const shell: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--lapo-bg)',
  color: 'var(--lapo-fg)',
  padding: 24,
  zIndex: 1000,
};

const card: CSSProperties = {
  width: 'min(440px, 100%)',
  background: 'var(--lapo-side-bg)',
  border: '1px solid var(--lapo-border)',
  borderRadius: 14,
  padding: 28,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
};

export function OAuthConsent({
  userEmail,
  clientName,
  scopes,
  loading = false,
  error,
  busy = false,
  onApprove,
  onDeny,
}: OAuthConsentProps): ReactElement {
  const appLabel = clientName ?? 'An application';
  return (
    <div style={shell} data-testid="oauth-consent">
      <div style={card}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 6px' }}>Authorize access</h1>
        <p style={{ color: 'var(--lapo-muted)', margin: '0 0 20px', fontSize: 14, lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--lapo-fg)' }}>{appLabel}</strong> wants access to your Lapo
          account{userEmail ? <> ({userEmail})</> : null}.
        </p>

        {loading && <p style={{ color: 'var(--lapo-muted)', fontSize: 14 }}>Loading…</p>}

        {error && (
          <p
            role="alert"
            style={{
              color: '#b42318',
              background: 'rgba(180,35,24,0.08)',
              border: '1px solid rgba(180,35,24,0.25)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 14,
              margin: '0 0 16px',
            }}
          >
            {error}
          </p>
        )}

        {!loading && !error && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'grid', gap: 12 }}>
            {scopes.map((s) => (
              <li key={s.scope} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span aria-hidden="true" style={{ color: 'var(--lapo-accent)', marginTop: 1 }}>
                  ✓
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 500 }}>{s.title}</span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--lapo-muted)' }}>
                    {s.description}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onDeny}
            disabled={busy}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid var(--lapo-border)',
              background: 'transparent',
              color: 'var(--lapo-fg)',
              fontSize: 14,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Deny
          </button>
          <button
            type="button"
            onClick={onApprove}
            disabled={busy || loading || !!error}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid var(--lapo-accent)',
              background: 'var(--lapo-accent)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
              cursor: busy || loading || error ? 'default' : 'pointer',
              opacity: busy || loading || error ? 0.6 : 1,
            }}
          >
            {busy ? 'Authorizing…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}
